import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import axios from "axios";
import {
    PANDORA_API,
    doesApiVersionMatch,
    MARKETS_API,
    MPC_API,
    Result,
    RUBY_API,
    IS_LOCAL_NX_DEV,
    LOCAL_DEV_API_KEY
} from "@bridgesplit/utils";
import { USER_WALLET_HEADER, API_VERSION_HEADER } from "@bridgesplit/abf-sdk";

import { AuthSliceState, updateApiVersionNeeded } from "./authSlice";

export enum AccessLevel {
    WalletRequired = "WALLET_REQUIRED",
    WalletNotRequired = "WALLET_NOT_REQUIRED",
    Unauthenticated = "UNAUTHENTICATED"
}
export interface AuthConfig {
    authLevel: AccessLevel;
}

interface HeadersResult {
    skip: boolean;
    headers: Record<string, string | undefined>;
}
export const DEFAULT_AUTH_CONFIG: AuthConfig = {
    authLevel: AccessLevel.WalletRequired
};

function getHeaders(config: AuthConfig, state: { authSlice: AuthSliceState }): HeadersResult {
    const userWallet = state.authSlice.stateWalletAddress;

    const skip = config.authLevel === AccessLevel.WalletRequired && !userWallet;
    const headers: Record<string, string | undefined> = {
        [USER_WALLET_HEADER]: config.authLevel === AccessLevel.Unauthenticated ? undefined : userWallet ?? undefined
    };

    return {
        skip,
        headers
    };
}
export type AbfBaseQueryFn = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;

interface EndpointDefinition {
    authConfig?: AuthConfig;
}

export const getBaseQuery = (baseUrl: string, defaultAuthConfig: AuthConfig = DEFAULT_AUTH_CONFIG): AbfBaseQueryFn => {
    const rawBaseQuery = fetchBaseQuery({
        baseUrl,
        prepareHeaders: (headers, { endpoint, getState }) => {
            const endpointDef = endpoint as EndpointDefinition;
            const endpointAuthConfig = endpointDef.authConfig || defaultAuthConfig;

            const state = getState() as { authSlice: AuthSliceState };
            const { headers: cookieHeaders } = getHeaders(endpointAuthConfig, state);

            Object.entries(cookieHeaders).forEach(([key, value]) => {
                if (value !== undefined) {
                    headers.set(key, value);
                }
            });

            // Add local dev API key header to bypass rate limiting during local development and E2E tests
            if (IS_LOCAL_NX_DEV && LOCAL_DEV_API_KEY) {
                headers.set("X-Local-Dev-Api-Key", LOCAL_DEV_API_KEY);
            }

            return headers;
        }
    });

    return async (args, api, extraOptions) => {
        const endpoint = api.endpoint as EndpointDefinition;
        const endpointAuthConfig = endpoint.authConfig || defaultAuthConfig;
        const { skip } = getHeaders(endpointAuthConfig, api.getState() as { authSlice: AuthSliceState });

        if (skip) {
            // Return error instead of aborting to prevent "ghost query" bug:
            //
            // Before: If a query was called with skip=false (hook level) but aborted here
            // (no wallet), RTK Query never registered it. When wallet connected and we called
            // resetApi(), there was no cached query to reset. The query stayed uninitialized
            // forever, never refetching even after wallet connection.
            //
            // Now: Returning an error keeps the query registered in RTK cache, so resetApi()
            // and cache invalidation work correctly when wallet state changes.
            return { error: { status: 401, data: "Wallet required" } };
        }

        const result = await rawBaseQuery(args, api, extraOptions);

        if (result.meta?.response) {
            const apiVersion = result.meta.response.headers.get(API_VERSION_HEADER);

            const url = result.meta.request.url;

            if (apiVersion && !doesApiVersionMatch(url, apiVersion)) {
                api.dispatch(updateApiVersionNeeded(true));
            }
        }

        if (result.error) {
            logError(result.error.data);
        }

        return result;
    };
};

const ALLOWED_ERRORS = [
    "Invalid JWT",
    "User does not have access",
    "User-Wallet: Value not in header",
    "User does not have beta access",
    "User has invalid roles"
];
function logError(error: unknown) {
    if (typeof error !== "string") return;
    for (const allowedError of ALLOWED_ERRORS) {
        if (error.toLowerCase().includes(allowedError.toLowerCase())) {
            return Result.err(error);
        }
    }
}

// serialize with header to force each query to be user specific in case of account change
export function abfSerializeQueryArgs({
    queryArgs,
    endpointName
}: {
    endpointName: string;
    queryArgs: FetchArgs | string;
}) {
    const params = JSON.stringify(queryArgs);

    return `${endpointName}(${params})`;
}

// to override query args for endpoints uncorrelated to user
export function unauthenticatedSerializeQueryArgs<T>({
    queryArgs,
    endpointName
}: {
    endpointName: string;
    queryArgs: T;
}) {
    const params = JSON.stringify(queryArgs);
    return `${endpointName}(${params})`;
}

export const mpcBaseQuery = getBaseQuery(MPC_API);
export const pandoraBaseQuery = getBaseQuery(PANDORA_API);

export const marketsBaseQuery = getBaseQuery(MARKETS_API, { authLevel: AccessLevel.WalletRequired });
export const marketsPublicBaseQuery = getBaseQuery(MARKETS_API, { authLevel: AccessLevel.WalletNotRequired });
export const marketsUnauthenticatedBaseQuery = getBaseQuery(MARKETS_API, { authLevel: AccessLevel.Unauthenticated });

export const rubyBaseQuery = getBaseQuery(RUBY_API, { authLevel: AccessLevel.WalletRequired });
export const rubyPublicBaseQuery = getBaseQuery(RUBY_API, { authLevel: AccessLevel.WalletNotRequired });

export function axiosCreateMarkets(baseURL = MARKETS_API) {
    const headers = getHeaders(DEFAULT_AUTH_CONFIG, {
        authSlice: { stateWalletAddress: undefined, walletClientType: undefined, apiVersionUpdateNeeded: false }
    }).headers;
    return axios.create({ baseURL, headers });
}
